import React, { useEffect, useRef } from "react";
import "./CslGlobe.css";

/* ═══════════════════════════════════════════════════════════════
   CslGlobe — a live, spinning dotted Earth on <canvas>.
   No images, no dependencies, no WebGL.

   Props (all optional):
     opacity   0.42   overall strength as a background
     speed     1      1 = one rotation per ~48s
     tilt      16     axial tilt in degrees
     rings     true   orbital rings + travelling pulses
   ═══════════════════════════════════════════════════════════════ */

const LAND = [
  [[-17,21],[-6,36],[10,37],[24,33],[35,31],[43,12],[51,12],[43,-1],[40,-16],[33,-26],[20,-35],[15,-28],[12,-6],[9,4],[-8,5],[-17,14]],
  [[-9,43],[-5,36],[3,42],[12,38],[18,42],[28,41],[31,46],[40,48],[33,58],[28,66],[20,70],[10,59],[4,53],[-5,50],[-9,43]],
  [[31,46],[45,40],[50,25],[58,24],[66,25],[70,20],[73,17],[78,8],[82,16],[90,22],[95,16],[100,13],[106,10],[109,20],[120,23],[122,31],[127,40],[131,45],[136,55],[146,60],[160,60],[170,66],[180,69],[160,73],[140,73],[110,76],[90,76],[70,73],[60,70],[50,68],[35,65],[31,58]],
  [[68,23],[72,20],[76,9],[80,10],[85,20],[89,22],[80,26],[72,25]],
  [[-168,66],[-160,58],[-150,60],[-135,58],[-125,49],[-121,36],[-110,24],[-97,17],[-90,20],[-83,10],[-79,9],[-81,25],[-75,35],[-70,42],[-60,47],[-55,52],[-65,60],[-80,70],[-100,70],[-130,70],[-155,71]],
  [[-79,9],[-75,1],[-80,-5],[-70,-18],[-70,-30],[-73,-45],[-75,-52],[-68,-55],[-62,-40],[-58,-34],[-48,-25],[-39,-20],[-35,-8],[-50,0],[-60,5],[-70,11]],
  [[113,-22],[114,-33],[125,-33],[135,-35],[141,-38],[150,-37],[153,-28],[145,-15],[136,-12],[130,-12],[125,-14]],
  [[-45,60],[-53,68],[-56,76],[-40,83],[-24,80],[-20,70],[-40,60]],
  [[172,-41],[174,-46],[168,-46],[170,-38],[175,-37]],
  [[95,5],[105,-6],[115,-8],[130,-8],[140,-8],[140,-2],[120,0],[105,3]],
  [[130,32],[138,35],[142,42],[145,44],[140,38],[133,34]],
  [[-6,50],[-5,58],[-2,58],[0,52],[-5,50]],
  [[43,-16],[50,-16],[48,-25],[44,-22]],
];

const RAD = Math.PI / 180;

function inRing(lon, lat, ring) {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      hit = !hit;
    }
  }
  return hit;
}

const isLand = (lon, lat) => LAND.some((r) => inRing(lon, lat, r));

function buildDots(stepDeg, landOnly) {
  const out = [];
  for (let lat = -84; lat <= 84; lat += stepDeg) {
    const circ = Math.cos(lat * RAD);
    if (circ <= 0.02) continue;
    const lonStep = stepDeg / Math.max(circ, 0.18);
    for (let lon = -180; lon < 180; lon += lonStep) {
      if (landOnly && !isLand(lon, lat)) continue;
      const la = lat * RAD;
      const lo = lon * RAD;
      out.push([
        Math.cos(la) * Math.cos(lo),
        Math.sin(la),
        Math.cos(la) * Math.sin(lo),
      ]);
    }
  }
  return out;
}

export default function CslGlobe({
  opacity = 0.42,
  speed = 1,
  tilt = 16,
  rings = true,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const land = buildDots(3, true);
    const mesh = buildDots(4.4, false);

    const ringDefs = [
      [78, 8, 1.30, 150],
      [66, -22, 1.52, 170],
      [84, 26, 1.16, 130],
    ];
    const ringPts = ringDefs.map(([rx, rz, rs, n]) => {
      const pts = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        let x = Math.cos(a) * rs;
        let y = 0;
        let z = Math.sin(a) * rs;
        const cx1 = Math.cos(rx * RAD);
        const sx1 = Math.sin(rx * RAD);
        [y, z] = [y * cx1 - z * sx1, y * sx1 + z * cx1];
        const cz = Math.cos(rz * RAD);
        const sz = Math.sin(rz * RAD);
        [x, y] = [x * cz - y * sz, x * sz + y * cz];
        pts.push([x, y, z]);
      }
      return pts;
    });

    let w = 0;
    let h = 0;
    let cx = 0;
    let cy = 0;
    let R = 0;
    let dpr = 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(rect.width, 1);
      h = Math.max(rect.height, 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = w / 2;
      cy = h / 2;
      R = Math.min(w, h) * 0.34;
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const T = tilt * RAD;
    const cosT = Math.cos(T);
    const sinT = Math.sin(T);

    const project = (p, cosS, sinS) => {
      const x = p[0] * cosS + p[2] * sinS;
      const z0 = -p[0] * sinS + p[2] * cosS;
      const y = p[1] * cosT - z0 * sinT;
      const z = p[1] * sinT + z0 * cosT;
      return [x, y, z];
    };

    let raf = 0;
    let spin = 0;
    let last = performance.now();
    let running = true;

    const draw = (now) => {
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      if (!reduced) spin += dt * 0.131 * speed;

      ctx.clearRect(0, 0, w, h);
      const cosS = Math.cos(spin);
      const sinS = Math.sin(spin);

      /* atmosphere */
      const glow = ctx.createRadialGradient(cx, cy, R * 0.82, cx, cy, R * 1.22);
      glow.addColorStop(0, "rgba(70,130,255,0)");
      glow.addColorStop(0.55, "rgba(80,150,255,0.09)");
      glow.addColorStop(1, "rgba(80,150,255,0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(cx, cy, R * 1.22, 0, Math.PI * 2);
      ctx.fill();

      /* ── ocean substrate ──
         Deliberately faint. It exists to keep the sphere solid
         when the Pacific faces us; if it competes with the land
         dots the whole thing reads as a plain dotted ball. */
      for (let i = 0; i < mesh.length; i++) {
        const [x, y, z] = project(mesh[i], cosS, sinS);
        const dep = (z + 1) / 2;
        const a = z >= 0 ? 0.028 + 0.10 * dep * dep : 0.018;
        ctx.fillStyle = `rgba(96,146,235,${a})`;
        ctx.fillRect(cx + x * R - 0.55, cy - y * R - 0.55, 1.1, 1.1);
      }

      /* ── landmasses ── brighter, larger, higher-contrast than
         the mesh so continents actually read as continents */
      for (let pass = 0; pass < 2; pass++) {
        for (let i = 0; i < land.length; i++) {
          const [x, y, z] = project(land[i], cosS, sinS);
          if (pass === 0 ? z >= 0 : z < 0) continue;
          const dep = (z + 1) / 2;
          const sx = cx + x * R;
          const sy = cy - y * R;
          if (z < 0) {
            ctx.fillStyle = `rgba(84,124,214,${0.08 + 0.08 * dep})`;
            ctx.fillRect(sx - 0.7, sy - 0.7, 1.4, 1.4);
          } else {
            const r = R * (0.0040 + 0.0073 * dep);
            ctx.fillStyle = `rgba(${Math.round(120 + 90 * dep)},${Math.round(
              195 + 55 * dep,
            )},255,${Math.min(0.30 + 0.7 * Math.pow(dep, 1.35), 1)})`;
            ctx.beginPath();
            ctx.arc(sx, sy, r, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      /* limb highlight */
      ctx.strokeStyle = "rgba(120,190,255,0.26)";
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();

      /* orbital rings */
      if (rings) {
        for (let ri = 0; ri < ringPts.length; ri++) {
          const rSpin = spin * (0.55 + ri * 0.28);
          const rc = Math.cos(rSpin);
          const rs = Math.sin(rSpin);
          const pts = ringPts[ri];
          for (let i = 0; i < pts.length; i++) {
            const [x, y, z] = project(pts[i], rc, rs);
            const dep = (z + 1) / 2;
            ctx.fillStyle = `rgba(110,180,255,${0.09 + 0.36 * dep * dep})`;
            const s = 0.9 + 1.0 * dep;
            ctx.fillRect(cx + x * R - s / 2, cy - y * R - s / 2, s, s);
          }
          const head = (now / 1000) * (0.11 + ri * 0.045);
          for (let k = 0; k < 16; k++) {
            const idx = Math.floor((head * pts.length + k) % pts.length);
            const [x, y, z] = project(pts[idx], rc, rs);
            const dep = (z + 1) / 2;
            const fade = (1 - k / 16) * (0.25 + 0.75 * dep);
            ctx.fillStyle = `rgba(168,214,255,${0.5 * fade})`;
            const s = 1.5 + 1.4 * dep * (1 - k / 16);
            ctx.beginPath();
            ctx.arc(cx + x * R, cy - y * R, s / 2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      if (running) raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [speed, tilt, rings]);

  return (
    <div className={`csl-globe ${className}`.trim()} style={{ opacity }} aria-hidden="true">
      <canvas ref={canvasRef} className="csl-globe__canvas" />
    </div>
  );
}